import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useCustomer } from '../context/CustomerContext';
import './CustomerLogin.css';

const CustomerLogin = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingCustomerData, setPendingCustomerData] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    otp: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const navigate = useNavigate();
  const { login } = useCustomer();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Use Supabase Auth for login
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      // Check if email is confirmed
      if (!data.user.email_confirmed_at) {
        setError('Please check your email and click the confirmation link before logging in.');
        setLoading(false);
        return;
      }

      // Check if customer exists in customers table
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('email', formData.email)
        .single();

      if (customerError || !customerData) {
        setError('No customer account found with this email address.');
        setLoading(false);
        return;
      }

      if (customerData.status !== 'active') {
        setError('Account is not active. Please contact support.');
        setLoading(false);
        return;
      }

      // Store customer data in localStorage
      localStorage.setItem('customerId', customerData.id);
      localStorage.setItem('customerEmail', customerData.email);
      localStorage.setItem('customerName', `${customerData.first_name} ${customerData.last_name}`);
      
      // Update context
      login(customerData);
      
      setSuccess('Login successful! Redirecting...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      console.error('Login error:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Prevent signup if already in OTP verification
    if (showOTPVerification) {
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      setLoading(false);
      return;
    }

    try {
      // Check if customer already exists
      const { data: existingCustomers, error: checkError } = await supabase
        .from('customers')
        .select('id, status')
        .eq('email', formData.email);

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Check error:', checkError);
        setError('An error occurred while checking customer status. Please try again.');
        setLoading(false);
        return;
      }

      if (existingCustomers && existingCustomers.length > 0) {
        setError('A customer account with this email already exists. Please login instead.');
        setLoading(false);
        return;
      }

      // Use Supabase Auth to send OTP
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: 'https://inifiy-2bc83.web.app/customer/login',
          data: {
            first_name: formData.first_name,
            last_name: formData.last_name,
            phone: formData.phone
          }
        }
      });

      if (authError) {
        setError(authError.message);
        setLoading(false);
        return;
      }

      // Store pending customer data
      const customerData = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        status: 'active'
      };

      setPendingCustomerData(customerData);
      setPendingEmail(formData.email);
      
      setSuccess('OTP sent to your email! Please check your inbox and enter the 6-digit code.');
      setShowOTPVerification(true);
      setFormData({
        ...formData,
        otp: ''
      });

    } catch (error) {
      console.error('Signup error:', error);
      setError('An error occurred during signup. Please try again.');
    }

    setLoading(false);
  };

  const handleOTPVerification = async (e) => {
    e.preventDefault();
    
    if (loading) return;
    
    setLoading(true);
    setError('');

    try {
      // Verify OTP with Supabase Auth
      const { data, error } = await supabase.auth.verifyOtp({
        email: pendingEmail,
        token: formData.otp,
        type: 'signup'
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      // Create customer account in customers table
      const { data: newCustomer, error: insertError } = await supabase
        .from('customers')
        .insert([pendingCustomerData])
        .select()
        .single();

      if (insertError) {
        console.error('Insert error:', insertError);
        throw insertError;
      }

      setSuccess('Email verified successfully! Your account has been created. You can now login.');
      setShowOTPVerification(false);
      setIsLogin(true);
      setPendingEmail('');
      setPendingCustomerData(null);
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        first_name: '',
        last_name: '',
        phone: '',
        otp: ''
      });

    } catch (error) {
      console.error('OTP verification error:', error);
      setError('An error occurred during verification. Please try again.');
    }

    setLoading(false);
  };

  const resendOTP = async () => {
    if (loading) return;
    
    setLoading(true);
    setError('');

    try {
      // Resend OTP using Supabase Auth
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: pendingEmail
      });

      if (error) {
        setError(error.message);
      } else {
        setSuccess('New OTP sent to your email! Please check your inbox.');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      setError('Failed to resend OTP. Please try again.');
    }

    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Check if customer exists first
      const { data: customer, error: customerError } = await supabase
        .from('customers')
        .select('id, status')
        .eq('email', formData.email)
        .single();

      if (customerError || !customer) {
        setError('No customer account found with this email address.');
        setLoading(false);
        return;
      }

      // Check if customer is active
      if (customer.status !== 'active') {
        setError('Account is not active. Please contact support.');
        setLoading(false);
        return;
      }

      // Use Supabase Auth's built-in password reset
      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: 'https://inifiy-2bc83.web.app/customer/reset-password'
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      setSuccess(`Password reset email sent to ${formData.email}. Please check your inbox and click the reset link.`);
      setShowForgotPassword(false);
      setIsLogin(true);
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        first_name: '',
        last_name: '',
        phone: '',
        otp: ''
      });

    } catch (error) {
      console.error('Forgot password error:', error);
      setError('An error occurred. Please try again.');
    }

    setLoading(false);
  };

  const showForgotPasswordForm = () => {
    setShowForgotPassword(true);
    setError('');
    setSuccess('');
  };

  const backToLogin = () => {
    setShowForgotPassword(false);
    setShowOTPVerification(false);
    setIsLogin(true);
    setPendingEmail('');
    setPendingCustomerData(null);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      first_name: '',
      last_name: '',
      phone: '',
      otp: ''
    });
    setError('');
    setSuccess('');
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setShowForgotPassword(false);
    setShowOTPVerification(false);
    setPendingEmail('');
    setPendingCustomerData(null);
    setError('');
    setSuccess('');
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      first_name: '',
      last_name: '',
      phone: ''
    });
  };

  // Render OTP verification form
  if (showOTPVerification) {
    return (
      <div className="customer-login-page">
        <div className="customer-login-container">
          <div className="login-header">
            <h2>Email Verification</h2>
            <p>Enter the OTP code sent to your email</p>
          </div>

          <form onSubmit={handleOTPVerification} className="login-form">
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="form-group">
              <label htmlFor="otp">OTP Code</label>
              <input
                type="text"
                id="otp"
                name="otp"
                value={formData.otp}
                onChange={handleInputChange}
                required
                placeholder="Enter 6-digit OTP"
                maxLength="6"
                pattern="[0-9]{6}"
              />
            </div>

            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify Email'}
            </button>
          </form>

          <div className="auth-info">
            <p>
              Didn't receive the OTP? 
              <button 
                className="link-btn" 
                onClick={resendOTP}
                disabled={loading}
              >
                Resend OTP
              </button>
            </p>
            <p>
              <button 
                className="link-btn" 
                onClick={backToLogin}
              >
                Back to login
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render forgot password form
  if (showForgotPassword) {
    return (
      <div className="customer-login-page">
        <div className="customer-login-container">
          <div className="login-header">
            <h2>Forgot Password</h2>
            <p>Enter your email to reset your password</p>
          </div>

          <form onSubmit={handleForgotPassword} className="login-form">
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter your email"
              />
            </div>

            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          <div className="auth-info">
            <p>
              Remember your password? 
              <button 
                className="link-btn" 
                onClick={backToLogin}
              >
                Back to login
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-login-page">
      <div className="customer-login-container">
        <div className="login-header">
          <h2>{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
          <p>{isLogin ? 'Sign in to your account' : 'Join us and start shopping'}</p>
        </div>

        <form onSubmit={isLogin ? handleLogin : handleSignup} className="login-form">
          {!isLogin && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name">First Name</label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required={!isLogin}
                  placeholder="Enter your first name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="last_name">Last Name</label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required={!isLogin}
                  placeholder="Enter your last name"
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="Enter your email"
            />
          </div>

          {!isLogin && (
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter your phone number"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                placeholder="Enter your password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                    <path d="M12 9c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
                    <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {!isLogin && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required={!isLogin}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex="-1"
                  title={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      <path d="M12 9c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
                      <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {isLogin && (
            <div className="forgot-password-link">
              <button
                type="button"
                className="link-btn"
                onClick={showForgotPasswordForm}
              >
                Forgot Password?
              </button>
            </div>
          )}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        <div className="login-footer">
          <p>
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button type="button" className="toggle-btn" onClick={toggleMode}>
              {isLogin ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin; 